// @ts-nocheck
/*
 * Port of views/dashboard/voice-plan/_form.js (origin/main-dev, 702 lines).
 * BYTE-IDENTICAL — MAX_PULSE_PRICE_DECIMALS=4, per-row lock/unlock state,
 * "Select all legs" toggle, call type + leg type dropdowns with dynamic
 * filtering, add-button visibility rules, pulse duration dropdown, price
 * input with 4-decimal cap + <1000 bound + onBlur normalize to 2dp,
 * complete-check + status badge in footer. VoicePlanPageHeader exported.
 */
import React, { useState, useEffect, useRef } from 'react';
import { PULSE_DURATION, PLAN_TYPE } from '@/utilities/constant';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Form: any = {};
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
import {
  getVoicePlanKey,
  isCallTypeAvailable,
  getLegTypeLabel,
  getLegTypesForPlanType,
  areAllPlansComplete,
  isPlanRowComplete,
  buildAllVoicePlanLegEntries,
  buildVoicePlanLegEntry,
  getAllVoicePlanLegKeys,
} from '@/utilities/voicePlanUtils';
import {
  FaPlus,
  FaInfoCircle,
  FaFileAlt,
  FaPhoneAlt,
  FaHeadset,
  FaGlobe,
  FaRobot,
  FaMinusCircle,
  FaPencilAlt,
  FaTrashAlt,
  FaUndo,
  FaCheckCircle,
  FaWaveSquare,
  FaLock,
} from 'react-icons/fa';
import '@/features/voice-plan/voice-plan-form.scss';

export const MAX_PULSE_PRICE_DECIMALS = 4;

const CALL_TYPE_BADGE_CLASS = {
  INBOUND: 'vp-call-badge--inbound',
  OUTBOUND: 'vp-call-badge--outbound',
  MISSED: 'vp-call-badge--missed',
};

function formatPulseDurationLabel(label) {
  if (!label) return label;
  return label.replace(/\bSec\b/, 'Seconds');
}

function formatPulseDurationValue(duration) {
  const match = PULSE_DURATION.find((d) => d.value === Number(duration));
  return match ? formatPulseDurationLabel(match.label) : `${duration} Seconds`;
}

function formatPulsePriceDisplay(price) {
  const num = Number(price);
  if (Number.isNaN(num)) return String(price ?? '');
  return num.toFixed(2);
}

function LegTypeCell({ legType }) {
  if (!legType) {
    return (
      <span className="vp-leg-type">
        <span className="vp-leg-icon vp-leg-icon--na">
          <FaMinusCircle />
        </span>
        N/A
      </span>
    );
  }

  const value = String(legType).toUpperCase();
  if (value === 'CUSTOMER_PSTN') {
    return (
      <span className="vp-leg-type">
        <span className="vp-leg-icon vp-leg-icon--customer">
          <FaPhoneAlt />
        </span>
        Customer PSTN
      </span>
    );
  }
  if (value === 'AGENT_PSTN') {
    return (
      <span className="vp-leg-type">
        <span className="vp-leg-icon vp-leg-icon--agent-pstn">
          <FaHeadset />
        </span>
        Agent PSTN
      </span>
    );
  }
  if (value === 'AGENT_WEBRTC') {
    return (
      <span className="vp-leg-type">
        <span className="vp-leg-icon vp-leg-icon--agent-webrtc">
          <FaGlobe />
        </span>
        Agent WebRTC
      </span>
    );
  }
  if (value === 'AI_AGENT') {
    return (
      <span className="vp-leg-type">
        <span className="vp-leg-icon vp-leg-icon--ai-agent">
          <FaRobot />
        </span>
        AI Agent
      </span>
    );
  }

  return (
    <span className="vp-leg-type">
      <span className="vp-leg-icon vp-leg-icon--na">
        <FaMinusCircle />
      </span>
      {getLegTypeLabel(legType)}
    </span>
  );
}

export function VoicePlanPageHeader({ title, subtitle }) {
  return (
    <div className="vp-page-header">
      <div className="vp-page-header__icon">
        <FaWaveSquare />
      </div>
      <div>
        <h4 className="vp-page-header__title">{title}</h4>
        <p className="vp-page-header__subtitle">{subtitle}</p>
      </div>
    </div>
  );
}

export default function RenderVoicePlanForm(props) {
  const [planNameError, setPlanNameError] = useState('');
  const [legTypeError, setLegTypeError] = useState('');
  const [addError, setAddError] = useState('');
  const [highlightedRow, setHighlightedRow] = useState(null);
  const lockedRowKeys = props.lockedRowKeys ?? [];
  const setLockedRowKeys = props.setLockedRowKeys ?? (() => {});
  const [selectAllLegs, setSelectAllLegs] = useState(() => {
    const allKeys = getAllVoicePlanLegKeys(props.allowedPlanTypes);
    return (
      allKeys.length > 0 &&
      allKeys.every((key) => (props.currentPlan || []).includes(key)) &&
      (props.plansData || []).length === allKeys.length
    );
  });
  const didAutoLock = useRef(false);

  useEffect(() => {
    if (props.setPlanNameError) {
      props.setPlanNameError(planNameError);
    }
  }, [planNameError, props]);

  useEffect(() => {
    if (
      props.autoExpandExistingRows &&
      props.plansData.length > 0 &&
      !didAutoLock.current
    ) {
      setLockedRowKeys(props.plansData.map((plan) => getVoicePlanKey(plan)));
      didAutoLock.current = true;
    }
  }, [props.autoExpandExistingRows, props.plansData]);

  const frozenLegKeys = props.frozenLegKeys ?? [];
  const isRowFrozen = (data) => frozenLegKeys.includes(getVoicePlanKey(data));
  const isRowLocked = (data) => isRowFrozen(data) || lockedRowKeys.includes(getVoicePlanKey(data));

  const updatePlanRow = (index, updates) => {
    props.setPlansData((prev) => prev.map((p, i) => (i === index ? { ...p, ...updates } : p)));
  };

  const lockRow = (data) => {
    const key = getVoicePlanKey(data);
    setLockedRowKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const unlockRow = (data) => {
    const key = getVoicePlanKey(data);
    setLockedRowKeys((prev) => prev.filter((k) => k !== key));
  };

  const typePool = Array.isArray(props.allowedPlanTypes)
    ? PLAN_TYPE.filter((item) => props.allowedPlanTypes.includes(item.value))
    : PLAN_TYPE;
  const filteredPlan = typePool.filter((item) =>
    isCallTypeAvailable(item.value, props.currentPlan || []),
  );
  const filteredLegTypes = getLegTypesForPlanType(props.planType).filter(
    (item) => !(props.currentPlan || []).includes(`${props.planType}_${item.value}`),
  );

  const requiresLegType = props.planType === 'INBOUND' || props.planType === 'OUTBOUND';
  const allLegsAddedForType = requiresLegType && filteredLegTypes.length === 0;
  const legTypeDropdownDisabled = !props.planType || !requiresLegType || allLegsAddedForType;
  const legTypePlaceholder = !props.planType
    ? 'Select Call Type first'
    : !requiresLegType
      ? 'N/A for Missed calls'
      : allLegsAddedForType
        ? 'All legs added'
        : 'Select Leg Type';
  const canShowAddButton = props.planType === 'MISSED' || (requiresLegType && props.legType);

  const allComplete = props.plansData.length > 0 && areAllPlansComplete(props.plansData);
  const allRowsSaved =
    props.plansData.length > 0 &&
    props.plansData.every((plan) => isRowLocked(plan));
  const canSubmit = allComplete && allRowsSaved;
  const missingPlanName = !String(props.planName || '').trim();
  const planNameBlocking = missingPlanName || !!planNameError;
  const allLegKeys = getAllVoicePlanLegKeys(props.allowedPlanTypes);
  const isAllLegsInTable =
    allLegKeys.length > 0 &&
    allLegKeys.every((key) => (props.currentPlan || []).includes(key)) &&
    props.plansData.length === allLegKeys.length;

  const allLegsFrozen =
    frozenLegKeys.length > 0 && isAllLegsInTable && props.plansData.every((plan) => isRowFrozen(plan));

  useEffect(() => {
    if (isAllLegsInTable) {
      setSelectAllLegs(true);
    }
  }, [isAllLegsInTable]);

  const handleSelectAllLegs = (checked) => {
    setSelectAllLegs(checked);
    setAddError('');
    setLegTypeError('');

    const frozenRows = props.plansData.filter((plan) => isRowFrozen(plan));

    if (checked) {
      const entries = buildAllVoicePlanLegEntries(props.allowedPlanTypes).map(
        (entry) => frozenRows.find((row) => getVoicePlanKey(row) === getVoicePlanKey(entry)) || entry,
      );
      const merged = [
        ...entries,
        ...frozenRows.filter((row) => !entries.includes(row)),
      ];
      props.setPlansData(merged);
      props.setCurrentPlan(merged.map((plan) => getVoicePlanKey(plan)));
      props.setPlanType?.('');
      props.setLegType?.('');
      setLockedRowKeys(frozenRows.map((plan) => getVoicePlanKey(plan)));
      return;
    }

    props.setPlansData(frozenRows);
    props.setCurrentPlan(frozenRows.map((plan) => getVoicePlanKey(plan)));
    props.setPlanType?.('');
    props.setLegType?.('');
    setLockedRowKeys(frozenRows.map((plan) => getVoicePlanKey(plan)));
  };

  const handleAddPlan = () => {
    setAddError('');
    if (planNameError) return;
    if (!props.planType) {
      setAddError('Select a call type first');
      return;
    }
    if (requiresLegType && !props.legType) {
      setLegTypeError('Leg Type is required');
      return;
    }
    setLegTypeError('');

    const newPlan = buildVoicePlanLegEntry(
      props.planType,
      requiresLegType ? props.legType : undefined,
    );

    const planKey = getVoicePlanKey(newPlan);
    if ((props.currentPlan || []).includes(planKey)) {
      setAddError('This leg is already added');
      return;
    }

    props.setCurrentPlan((prev) => [...prev, planKey]);
    props.setPlansData((prevData) => [...prevData, newPlan]);
    props.setLegType?.('');
  };

  const handleRemovePlan = (data) => {
    const key = getVoicePlanKey(data);
    setLockedRowKeys((prev) => prev.filter((k) => k !== key));
    const nextData = props.plansData.filter((plan) => getVoicePlanKey(plan) !== key);
    props.setPlansData(nextData);
    props.setCurrentPlan((prev) => prev.filter((plan) => plan !== key));
    const remainingKeys = nextData.map((plan) => getVoicePlanKey(plan));
    const allRemaining =
      allLegKeys.length > 0 &&
      remainingKeys.length === allLegKeys.length &&
      allLegKeys.every((k) => remainingKeys.includes(k));
    if (!allRemaining) {
      setSelectAllLegs(false);
    }
  };

  const handleReset = () => {
    if (props.onReset) {
      props.onReset();
      didAutoLock.current = false;
      setLockedRowKeys([]);
      setLegTypeError('');
      setAddError('');
      setHighlightedRow(null);
      setSelectAllLegs(false);
      return;
    }
    props.setPlanName('');
    props.setPlanType?.('');
    props.setLegType?.('');
    props.setPlansData([]);
    props.setCurrentPlan([]);
    setPlanNameError('');
    setLegTypeError('');
    setAddError('');
    setHighlightedRow(null);
    setLockedRowKeys([]);
    setSelectAllLegs(false);
    didAutoLock.current = false;
  };

  const handleEditRow = (data, index) => {
    unlockRow(data);
    setHighlightedRow(index);
    setTimeout(() => setHighlightedRow(null), 2000);
  };

  const handleSaveRow = (data) => {
    if (!isPlanRowComplete(data)) return;
    lockRow(data);
  };

  const handleAddAllLegs = () => {
    const keysToLock = (props.plansData || [])
      .filter((plan) => isPlanRowComplete(plan))
      .map((plan) => getVoicePlanKey(plan));
    if (keysToLock.length === 0) return;
    setLockedRowKeys((prev) => [...new Set([...(prev || []), ...keysToLock])]);
  };

  const canAddAllLegs = (props.plansData || []).some(
    (plan) => isPlanRowComplete(plan) && !isRowLocked(plan),
  );

  const addDisabled = planNameError || !canShowAddButton;

  return (
    <>
      <VoicePlanPageHeader
        title={props.pageTitle || 'New Voice Plan Information'}
        subtitle={
          props.pageSubtitle ||
          'Create and configure voice plan details including call types, legs, durations and pricing.'
        }
      />

      <div className="vp-form-row">
        <div className="vp-field">
          <label className="vp-label" htmlFor="planName">
            Plan Name<span className="vp-required">*</span>
          </label>
          <div className="vp-input-wrap">
            <Form.Control
              type="text"
              id="planName"
              className="vp-select"
              value={props.planName}
              placeholder="Plan Name"
              isInvalid={planNameBlocking && canSubmit}
              onChange={(e) => {
                const value = e.target.value;
                if (!/^[a-zA-Z0-9-_]*$/.test(value)) {
                  setPlanNameError(
                    'Only letters, numbers, hyphen (-), and underscore (_) are allowed',
                  );
                } else {
                  setPlanNameError('');
                }
                props.setPlanName(value);
              }}
            />
            <FaFileAlt className="vp-input-icon" />
          </div>
          {planNameError && <small className="vp-field-error">{planNameError}</small>}
          {!planNameError && missingPlanName && canSubmit && (
            <small className="vp-field-error">Please enter a plan name to proceed</small>
          )}
        </div>

        {props.setIsDemo && !props.isEdit && (
          <div className="vp-field">
            <label className="vp-label" htmlFor="is_demo">
              Demo Plan
            </label>
            <div
              className="vp-input-wrap"
              style={{ display: 'flex', alignItems: 'center', height: '38px' }}
            >
              <Form.Check
                type="checkbox"
                id="is_demo"
                label="Use as the demo voice plan"
                checked={!!props.isDemo}
                onChange={(e) => props.setIsDemo(e.target.checked)}
              />
            </div>
          </div>
        )}

        {!isAllLegsInTable && (
          <>
            <div className="vp-field">
              <label className="vp-label" htmlFor="call_type">
                Select Call Type<span className="vp-required">*</span>
              </label>
              <Form.Select
                id="call_type"
                className="vp-select"
                value={props.planType}
                onChange={(e) => {
                  props.setPlanType(e.target.value);
                  props.setLegType('');
                  setLegTypeError('');
                  setAddError('');
                }}
              >
                <option value="">Select Call Type</option>
                {filteredPlan.map((val, idx) => (
                  <option key={idx} value={val.value}>
                    {val.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="vp-field">
              <label className="vp-label" htmlFor="leg_type">
                Select Leg Type<span className="vp-required">*</span>
              </label>
              <Form.Select
                id="leg_type"
                className="vp-select"
                value={legTypeDropdownDisabled ? '' : props.legType || ''}
                disabled={legTypeDropdownDisabled}
                onChange={(e) => {
                  props.setLegType(e.target.value);
                  setLegTypeError('');
                  setAddError('');
                }}
              >
                <option value="">{legTypePlaceholder}</option>
                {!legTypeDropdownDisabled &&
                  filteredLegTypes.map((val, idx) => (
                    <option key={idx} value={val.value}>
                      {val.label}
                    </option>
                  ))}
              </Form.Select>
              {legTypeError && <small className="vp-field-error">{legTypeError}</small>}
            </div>

            {props.planType && (
              <div className="vp-field vp-field--add">
                <button
                  type="button"
                  disabled={addDisabled}
                  onClick={handleAddPlan}
                  title={
                    props.planType === 'MISSED'
                      ? 'Add missed call row'
                      : 'Add selected leg type to table'
                  }
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-primary bg-primary text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  <FaPlus />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {addError && <small className="vp-field-error d-block mb-2">{addError}</small>}

      <div className="vp-info-banner">
        <FaInfoCircle className="vp-info-banner__icon" />
        <span>
          {frozenLegKeys.length > 0
            ? 'This plan is allocated. Existing legs are locked and cannot be edited or removed — you can only add new legs.'
            : 'Add multiple legs for this voice plan. You can configure duration and pricing for each.'}
        </span>
      </div>

      <div className="vp-section-header">
        <div className="vp-section-header__left">
          <h5 className="vp-section-title">Assigned Voice Plan Legs</h5>
          <span className="vp-count-badge">{props.plansData.length}</span>
        </div>
        <label className="vp-select-all-legs">
          <Form.Check
            type="checkbox"
            id="selectAllLegs"
            checked={selectAllLegs}
            onChange={(e) => handleSelectAllLegs(e.target.checked)}
            label="Select all legs"
          />
        </label>
      </div>

      <div className="vp-table-wrap">
        <table className="vp-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Call Type</th>
              <th>Leg Type</th>
              <th>Pulse Duration</th>
              <th>Pulse Price (Paisa)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {props.plansData.length > 0 ? (
              props.plansData.map((data, index) => {
                const rowKey = getVoicePlanKey(data);
                const rowLocked = isRowLocked(data);
                const rowComplete = isPlanRowComplete(data);
                const canSaveRow = !rowLocked && rowComplete;

                return (
                  <tr key={rowKey} className={highlightedRow === index ? 'vp-row-highlight' : ''}>
                    <td>{index + 1}</td>
                    <td>
                      <span
                        className={`vp-call-badge ${CALL_TYPE_BADGE_CLASS[data.plan_type] || ''}`}
                      >
                        {data.plan_type}
                      </span>
                    </td>
                    <td>
                      <LegTypeCell legType={data.leg_type} />
                    </td>
                    <td>
                      {data.plan_type === 'MISSED' ? (
                        <span className="vp-na-text">N/A</span>
                      ) : rowLocked ? (
                        <span className="vp-readonly-value">
                          {formatPulseDurationValue(data.pulse_duration)}
                        </span>
                      ) : (
                        <Form.Select
                          size="sm"
                          className="vp-table-select"
                          value={data.pulse_duration || ''}
                          onChange={(e) =>
                            updatePlanRow(index, { pulse_duration: Number(e.target.value) })
                          }
                        >
                          <option value="">Select Duration</option>
                          {PULSE_DURATION.map((val, idx) => (
                            <option key={idx} value={val.value}>
                              {formatPulseDurationLabel(val.label)}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </td>
                    <td>
                      {rowLocked ? (
                        <span className="vp-readonly-value">
                          {formatPulsePriceDisplay(data.pulse_price)}
                        </span>
                      ) : (
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          size="sm"
                          className="vp-table-input"
                          placeholder="0.00"
                          value={String(data.pulse_price ?? '')}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!/^\d*\.?\d*$/.test(value)) return;
                            const decimalParts = value.split('.');
                            if (
                              decimalParts.length > 1 &&
                              decimalParts[1].length > MAX_PULSE_PRICE_DECIMALS
                            ) {
                              return;
                            }
                            if (value !== '' && value !== '.' && Number(value) > 1000) {
                              return;
                            }
                            updatePlanRow(index, { pulse_price: value });
                          }}
                          onBlur={(e) => {
                            const raw = e.target.value?.trim() ?? '';
                            if (!raw || raw === '.') return;
                            const numValue = parseFloat(raw);
                            if (Number.isNaN(numValue)) return;
                            const frac = raw.includes('.') ? raw.split('.')[1] : '';
                            const normalized =
                              frac.length > MAX_PULSE_PRICE_DECIMALS
                                ? numValue.toFixed(MAX_PULSE_PRICE_DECIMALS)
                                : numValue.toFixed(2);
                            updatePlanRow(index, { pulse_price: normalized });
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <div className="vp-actions">
                        {isRowFrozen(data) ? (
                          <span
                            className="vp-action-btn"
                            title="Plan is allocated — this leg can't be edited or removed"
                          >
                            <FaLock size={13} />
                          </span>
                        ) : !rowLocked ? (
                          <button
                            type="button"
                            title={
                              rowComplete
                                ? data.plan_type === 'MISSED'
                                  ? 'Save price'
                                  : 'Save duration and price'
                                : data.plan_type === 'MISSED'
                                  ? 'Enter price first'
                                  : 'Enter duration and price first'
                            }
                            disabled={!canSaveRow}
                            onClick={() => handleSaveRow(data)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-primary bg-primary text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                          >
                            <FaPlus size={13} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="vp-action-btn vp-action-btn--edit"
                            title="Edit"
                            onClick={() => handleEditRow(data, index)}
                          >
                            <FaPencilAlt size={13} />
                          </button>
                        )}
                        {!isRowFrozen(data) && (
                          <button
                            type="button"
                            className="vp-action-btn vp-action-btn--delete"
                            title="Delete leg"
                            onClick={() => handleRemovePlan(data)}
                          >
                            <FaTrashAlt size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="vp-empty-row">
                <td colSpan={6}>
                  No legs added yet — select call type and leg type, then click +
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="vp-footer">
        <div className="vp-footer__left">
          {props.showSubmitButton !== false && (
            <button
              type="button"
              disabled={!canAddAllLegs}
              onClick={handleAddAllLegs}
              title="Save duration and price for every complete leg"
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-primary bg-primary/10 px-4 text-xs font-semibold text-primary shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
            >
              <FaPlus />
              Add all legs
            </button>
          )}
          {props.showSubmitButton !== false && !allLegsFrozen && (
            <button
              type="submit"
              disabled={
                props.submitDisabled || !props.planName || planNameError || !canSubmit
              }
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-primary bg-primary px-5 text-xs font-bold text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
            >
              {props.submitLabel || 'Create Voice Plan'}
              {props.submitLoading && (
                <span
                  className="ml-1 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                  aria-hidden
                />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-border/60 bg-card px-4 text-xs font-semibold text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
          >
            <FaUndo />
            Reset
          </button>
        </div>
        <div className="vp-footer__right">
          <span
            className={`vp-status-badge ${
              canSubmit && planNameBlocking
                ? 'vp-status-badge--error'
                : canSubmit
                  ? ''
                  : 'vp-status-badge--pending'
            }`}
          >
            <FaCheckCircle className="vp-status-badge__icon" />
            {allLegsFrozen
              ? 'All legs are already added'
              : canSubmit && missingPlanName
              ? 'Please enter a plan name to proceed'
              : canSubmit && planNameError
                ? planNameError
                : !canSubmit
                  ? props.plansData.length === 0
                    ? 'Add at least one leg to continue'
                    : !allComplete
                      ? 'Fill pulse duration and price for all legs to continue'
                      : `Click + on all legs before ${props.isEdit ? 'updating' : 'creating'} the voice plan`
                  : 'All legs are ready to submit'}
          </span>
        </div>
      </div>

      {/* <div className="vp-create-note">
        <FaInfoCircle className="vp-create-note__icon" />
        <span>
          <strong>Note:</strong> Click the <strong>+</strong> button on all legs to add them before{' '}
          {props.isEdit ? 'updating' : 'creating'} the voice plan.
        </span>
      </div> */}
    </>
  );
}
